import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Alert,
  Snackbar,
  CircularProgress,
  Tooltip,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Radio,
  Person,
  Schedule,
  Image,
  CloudUpload,
  DeleteOutline
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { programService } from '../../services/programService';
import { userService } from '../../services/userService';

const ProgramManagement = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    day_of_week: 'monday',
    start_time: '',
    end_time: '',
    is_active: true,
    dj_id: ''
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [bannerDialog, setBannerDialog] = useState({ open: false, program: null });
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const [dimensionAlert, setDimensionAlert] = useState({ show: false, message: '' });
  const queryClient = useQueryClient();

  // Função para tratar mensagens de erro de forma amigável
  const getErrorMessage = (error) => {
    const errorMessage = error.response?.data?.error || error.message || 'Erro desconhecido';
    console.log('🔍 getErrorMessage - Error message:', errorMessage);
    
    // Mensagens específicas para diferentes tipos de erro
    if (errorMessage.includes('Conflito de horário')) {
      let friendlyMessage;
      if (errorMessage.includes('programação automática')) {
        friendlyMessage = '⚠️ Conflito de horário detectado! Já existe programação automática ativa neste horário. Por favor, escolha um horário diferente ou desative a programação automática para este dia.';
      } else {
        friendlyMessage = '⚠️ Conflito de horário detectado! Já existe um programa ativo neste horário. Por favor, escolha um horário diferente ou desative o programa conflitante.';
      }
      console.log('🔍 getErrorMessage - Returning friendly message:', friendlyMessage);
      return friendlyMessage;
    }
    
    if (errorMessage.includes('horário')) {
      return '⚠️ Erro de horário: Verifique se os horários de início e fim estão corretos.';
    }
    
    if (errorMessage.includes('DJ')) {
      return '⚠️ Erro relacionado ao DJ: Verifique se o DJ selecionado está ativo.';
    }
    
    if (errorMessage.includes('nome')) {
      return '⚠️ Erro no nome: Verifique se o nome do programa não está duplicado.';
    }
    
    // Mensagem padrão para outros erros
    return `❌ ${errorMessage}`;
  };

  // Buscar programas
  const { data: programsData, isLoading: programsLoading } = useQuery({
    queryKey: ['programs'],
    queryFn: () => programService.getAllPrograms(),
  });

  // Buscar DJs para o select
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => userService.getAllUsers(),
  });

  const programs = Array.isArray(programsData?.data?.programs) ? programsData.data.programs : [];
  const djs = Array.isArray(usersData?.data?.users) ? usersData.data.users.filter(user => user.role === 'dj') : [];

  // Mutations
  const createProgramMutation = useMutation({
    mutationFn: (data) => programService.createProgram(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['programs']);
      setSnackbar({ open: true, message: 'Programa criado com sucesso!', severity: 'success' });
      handleCloseDialog();
    },
    onError: (error) => {
      console.log('🔍 createProgramMutation onError - Error:', error);
      const friendlyMessage = getErrorMessage(error);
      console.log('🔍 createProgramMutation onError - Setting snackbar with message:', friendlyMessage);
      setSnackbar({ open: true, message: friendlyMessage, severity: 'error' });
    }
  });

  const updateProgramMutation = useMutation({
    mutationFn: ({ id, data }) => programService.updateProgram(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['programs']);
      setSnackbar({ open: true, message: 'Programa atualizado com sucesso!', severity: 'success' });
      handleCloseDialog();
    },
    onError: (error) => {
      setSnackbar({ open: true, message: getErrorMessage(error), severity: 'error' });
    }
  });

  const deleteProgramMutation = useMutation({
    mutationFn: (id) => programService.deleteProgram(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['programs']);
      setSnackbar({ open: true, message: 'Programa deletado com sucesso!', severity: 'success' });
    },
    onError: (error) => {
      setSnackbar({ open: true, message: getErrorMessage(error), severity: 'error' });
    }
  });

  const uploadBannerMutation = useMutation({
    mutationFn: ({ programId, formData }) => programService.uploadBanner(programId, formData),
    onSuccess: () => {
      queryClient.invalidateQueries(['programs']);
      setSnackbar({ open: true, message: 'Banner atualizado com sucesso!', severity: 'success' });
      handleCloseBannerDialog();
    },
    onError: (error) => {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Erro ao atualizar banner', severity: 'error' });
    }
  });

  const removeBannerMutation = useMutation({
    mutationFn: (programId) => programService.removeBanner(programId),
    onSuccess: () => {
      queryClient.invalidateQueries(['programs']);
      setSnackbar({ open: true, message: 'Banner removido com sucesso!', severity: 'success' });
      handleCloseBannerDialog();
    },
    onError: (error) => {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Erro ao remover banner', severity: 'error' });
    }
  });

  const handleOpenDialog = (program = null) => {
    if (program) {
      setEditingProgram(program);
      setFormData({
        name: program.name || '',
        description: program.description || '',
        day_of_week: program.day_of_week || 'monday',
        start_time: program.start_time || '',
        end_time: program.end_time || '',
        is_active: program.is_active !== undefined ? program.is_active : true,
        dj_id: program.dj_id || ''
      });
    } else {
      setEditingProgram(null);
      setFormData({
        name: '',
        description: '',
        day_of_week: 'monday',
        start_time: '',
        end_time: '',
        is_active: true,
        dj_id: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingProgram(null);
    setFormData({
      name: '',
      description: '',
      day_of_week: 'monday',
      start_time: '',
      end_time: '',
      is_active: true,
      dj_id: ''
    });
  };

  const handleSubmit = () => {
    // Prepare data for submission
    const submitData = {
      ...formData,
      dj_id: formData.dj_id ? parseInt(formData.dj_id) : undefined
    };
    
    if (editingProgram) {
      updateProgramMutation.mutate({ id: editingProgram.id, data: submitData });
    } else {
      createProgramMutation.mutate(submitData);
    }
  };

  const handleDelete = (id) => {
    if (window.confirm('Tem certeza que deseja deletar este programa?')) {
      deleteProgramMutation.mutate(id);
    }
  };

  const handleOpenBannerDialog = (program) => {
    setBannerDialog({ open: true, program });
    setSelectedFile(null);
    setPreviewUrl(program.banner_image || null);
  };

  const handleCloseBannerDialog = () => {
    setBannerDialog({ open: false, program: null });
    setSelectedFile(null);
    setPreviewUrl(null);
    setImageDimensions({ width: 0, height: 0 });
    setDimensionAlert({ show: false, message: '' });
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      
      // Validar dimensões da imagem
      const validateImageDimensions = (file) => {
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const img = document.createElement('img');
            img.onload = () => {
              const width = img.naturalWidth;
              const height = img.naturalHeight;
              resolve({ width, height });
            };
            img.onerror = () => {
              resolve({ width: 0, height: 0 });
            };
            img.src = e.target.result;
          };
          reader.onerror = () => {
            resolve({ width: 0, height: 0 });
          };
          reader.readAsDataURL(file);
        });
      };

      // Executar validação
      validateImageDimensions(file).then(({ width, height }) => {
        setImageDimensions({ width, height });
        
        // Verificar se as dimensões ultrapassam os limites recomendados
        const maxWidth = 1229;
        const maxHeight = 394;
        
        if (width > maxWidth || height > maxHeight) {
          setDimensionAlert({
            show: true,
            message: `⚠️ O banner selecionado (${width}x${height}px) ultrapassou os limites recomendados (${maxWidth}x${maxHeight}px). Carregue um banner no tamanho recomendado.`
          });
        } else {
          setDimensionAlert({ show: false, message: '' });
        }
      });
    }
  };

  const handleUploadBanner = () => {
    if (!selectedFile) return;
    
    const formData = new FormData();
    formData.append('banner', selectedFile);
    
    uploadBannerMutation.mutate({ 
      programId: bannerDialog.program.id, 
      formData 
    });
  };

  const handleRemoveBanner = () => {
    if (window.confirm('Tem certeza que deseja remover o banner deste programa?')) {
      removeBannerMutation.mutate(bannerDialog.program.id);
    }
  };

  const getDayLabel = (day) => {
    const days = {
      monday: 'Segunda-feira',
      tuesday: 'Terça-feira',
      wednesday: 'Quarta-feira',
      thursday: 'Quinta-feira',
      friday: 'Sexta-feira',
      saturday: 'Sábado',
      sunday: 'Domingo'
    };
    return days[day] || day;
  };

  const getStatusColor = (isActive) => {
    return isActive ? 'success' : 'error';
  };

  const getStatusLabel = (isActive) => {
    return isActive ? 'Ativo' : 'Inativo';
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    // Remove os segundos se existirem (formato HH:MM:SS -> HH:MM)
    return timeString.substring(0, 5);
  };

  if (programsLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress sx={{ color: '#FF6B35' }} />
      </Box>
    );
  }

  return (
    <Box>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5" sx={{ fontFamily: 'Orbitron', fontWeight: 700, color: '#FF6B35' }}>
              Gerenciar Programas
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              sx={{
                bgcolor: '#FF6B35',
                '&:hover': { bgcolor: '#FF8A65' }
              }}
            >
              Adicionar Programa
            </Button>
          </Box>

          <TableContainer component={Paper} sx={{ bgcolor: 'rgba(10, 10, 10, 0.5)' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>DJ</TableCell>
                  <TableCell>Nome do Programa</TableCell>
                  <TableCell>Banner</TableCell>
                  <TableCell>Dia da Semana</TableCell>
                  <TableCell>Horário</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {programs.map((program, index) => (
                  <TableRow key={program.id || `program-${index}`}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar
                          src={program.dj?.profile_image}
                          sx={{ width: 32, height: 32, bgcolor: '#FF6B35' }}
                        >
                          {program.dj?.name?.charAt(0)}
                        </Avatar>
                        <Typography variant="body2">
                          {program.dj?.name || 'N/A'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {program.name}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.7 }}>
                        {program.description}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {program.banner_image ? (
                        <Avatar
                          src={program.banner_image}
                          variant="rounded"
                          sx={{ width: 60, height: 40, cursor: 'pointer' }}
                          onClick={() => handleOpenBannerDialog(program)}
                        >
                          <Image />
                        </Avatar>
                      ) : (
                        <Avatar 
                          variant="rounded" 
                          sx={{ 
                            width: 60, 
                            height: 40, 
                            bgcolor: 'rgba(255, 107, 53, 0.1)',
                            cursor: 'pointer'
                          }}
                          onClick={() => handleOpenBannerDialog(program)}
                        >
                          <Image sx={{ color: '#FF6B35' }} />
                        </Avatar>
                      )}
                    </TableCell>
                    <TableCell>{getDayLabel(program.day_of_week)}</TableCell>
                    <TableCell>
                      {formatTime(program.start_time)} - {formatTime(program.end_time)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(program.is_active)}
                        color={getStatusColor(program.is_active)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="Editar Programa">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(program)}
                            sx={{ color: '#00D4FF' }}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Editar Banner">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenBannerDialog(program)}
                            sx={{ color: '#FF6B35' }}
                          >
                            <Image />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Deletar">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(program.id)}
                            sx={{ color: '#FF1744' }}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Dialog para Editar/Criar Programa */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingProgram ? 'Editar Programa' : 'Adicionar Programa'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Nome do Programa"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Descrição"
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              margin="normal"
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>DJ</InputLabel>
              <Select
                value={formData.dj_id}
                onChange={(e) => setFormData({ ...formData, dj_id: e.target.value })}
                label="DJ"
              >
                {djs.map((dj) => (
                  <MenuItem key={dj.id} value={dj.id}>
                    {dj.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel>Dia da Semana</InputLabel>
              <Select
                value={formData.day_of_week}
                onChange={(e) => setFormData({ ...formData, day_of_week: e.target.value })}
                label="Dia da Semana"
              >
                <MenuItem value="monday">Segunda-feira</MenuItem>
                <MenuItem value="tuesday">Terça-feira</MenuItem>
                <MenuItem value="wednesday">Quarta-feira</MenuItem>
                <MenuItem value="thursday">Quinta-feira</MenuItem>
                <MenuItem value="friday">Sexta-feira</MenuItem>
                <MenuItem value="saturday">Sábado</MenuItem>
                <MenuItem value="sunday">Domingo</MenuItem>
              </Select>
            </FormControl>
            <Box display="flex" gap={2} margin="normal">
              <TextField
                label="Horário de Início"
                type="time"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Horário de Fim"
                type="time"
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Programa Ativo"
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={createProgramMutation.isPending || updateProgramMutation.isPending}
            sx={{ bgcolor: '#FF6B35', '&:hover': { bgcolor: '#FF8A65' } }}
          >
            {(createProgramMutation.isPending || updateProgramMutation.isPending) ? 
              <CircularProgress size={20} /> : 'Salvar'
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para Editar Banner do Programa */}
      <Dialog open={bannerDialog.open} onClose={handleCloseBannerDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Editar Banner - {bannerDialog.program?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {/* Preview da imagem atual */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#FF6B35' }}>
                Banner Atual
              </Typography>
              {previewUrl ? (
                <Box sx={{ textAlign: 'center' }}>
                  <img
                    src={previewUrl}
                    alt="Preview do banner"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '200px',
                      borderRadius: '8px',
                      border: '2px solid #FF6B35'
                    }}
                  />
                </Box>
              ) : (
                <Box sx={{ 
                  textAlign: 'center', 
                  p: 4, 
                  border: '2px dashed #FF6B35', 
                  borderRadius: '8px',
                  bgcolor: 'rgba(255, 107, 53, 0.05)'
                }}>
                  <Image sx={{ fontSize: 48, color: '#FF6B35', mb: 1 }} />
                  <Typography variant="body2" sx={{ color: '#FF6B35' }}>
                    Nenhum banner definido
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Upload de nova imagem */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, color: '#FF6B35' }}>
                Nova Imagem
              </Typography>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="banner-upload"
                type="file"
                onChange={handleFileSelect}
              />
              <label htmlFor="banner-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUpload />}
                  sx={{
                    borderColor: '#FF6B35',
                    color: '#FF6B35',
                    '&:hover': {
                      borderColor: '#FF8A65',
                      color: '#FF8A65',
                      bgcolor: 'rgba(255, 107, 53, 0.05)'
                    }
                  }}
                >
                  Selecionar Imagem
                </Button>
              </label>
              {selectedFile && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" sx={{ color: '#00D4FF' }}>
                    Arquivo selecionado: {selectedFile.name}
                  </Typography>
                  {imageDimensions.width > 0 && imageDimensions.height > 0 && (
                    <Typography variant="body2" sx={{ color: '#00D4FF', mt: 0.5 }}>
                      Dimensões: {imageDimensions.width}x{imageDimensions.height}px
                    </Typography>
                  )}
                </Box>
              )}
              
              {/* Alerta de dimensões */}
              {dimensionAlert.show && (
                <Alert 
                  severity="warning" 
                  sx={{ 
                    mt: 2,
                    bgcolor: 'rgba(255, 152, 0, 0.1)',
                    border: '1px solid #FF9800',
                    '& .MuiAlert-message': {
                      color: '#FF9800'
                    }
                  }}
                >
                  {dimensionAlert.message}
                </Alert>
              )}
            </Box>

            {/* Informações sobre o arquivo */}
            <Box sx={{ 
              p: 2, 
              bgcolor: 'rgba(0, 212, 255, 0.05)', 
              borderRadius: '8px',
              border: '1px solid rgba(0, 212, 255, 0.2)'
            }}>
              <Typography variant="body2" sx={{ color: '#00D4FF', fontWeight: 600, mb: 1 }}>
                Informações:
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', color: '#00D4FF' }}>
                • Formatos aceitos: JPEG, JPG, PNG
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', color: '#00D4FF' }}>
                • Tamanho máximo: 5MB
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', color: '#00D4FF' }}>
                • Dimensões recomendadas: 1229x394px
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBannerDialog}>
            Cancelar
          </Button>
          {bannerDialog.program?.banner_image && (
            <Button
              onClick={handleRemoveBanner}
              variant="outlined"
              startIcon={<DeleteOutline />}
              disabled={removeBannerMutation.isPending}
              sx={{
                borderColor: '#FF1744',
                color: '#FF1744',
                '&:hover': {
                  borderColor: '#FF1744',
                  color: '#FF1744',
                  bgcolor: 'rgba(255, 23, 68, 0.05)'
                }
              }}
            >
              {removeBannerMutation.isPending ? <CircularProgress size={20} /> : 'Remover Banner'}
            </Button>
          )}
          <Button
            onClick={handleUploadBanner}
            variant="contained"
            disabled={!selectedFile || uploadBannerMutation.isPending}
            sx={{ 
              bgcolor: '#FF6B35', 
              '&:hover': { bgcolor: '#FF8A65' },
              '&:disabled': { bgcolor: 'rgba(255, 107, 53, 0.3)' }
            }}
          >
            {uploadBannerMutation.isPending ? <CircularProgress size={20} /> : 'Atualizar Banner'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={8000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ 
          zIndex: 9999,
          '& .MuiSnackbarContent-root': {
            backgroundColor: snackbar.severity === 'error' ? '#d32f2f' : '#2e7d32'
          }
        }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ 
            width: '100%',
            fontSize: '1rem',
            fontWeight: 500,
            '& .MuiAlert-message': {
              whiteSpace: 'pre-line'
            }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProgramManagement;
